import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { TimeSeriesAreaChartRecharts } from '../../../../shared/charts/recharts';
import type { ChartAnnotation } from '../../../../shared/charts/types';
import {
  buildValueLabelRow,
  formatTideHeightDisplay,
} from '../../helpers/graph/graphData';
import {
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_PLOT_HEIGHT,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
  buildWeatherChartBands,
  createWeatherChartXTickFormatter,
  type WeatherChartBaseProps,
} from './chartDefaults';
import WeatherTheme from './weather-theme';

export interface TideChartProps extends WeatherChartBaseProps {
  annotations?: ChartAnnotation[];
}

const TideChart: FC<TideChartProps> = ({
  data,
  domain,
  timeZone,
  annotations = [],
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();
  const unitsConfig = useSelector(selectUnitsConfig);

  const backgroundBands = useMemo(
    () => buildWeatherChartBands(domain, timeZone),
    [domain, timeZone],
  );

  const labelRows = useMemo(
    () => [
      buildValueLabelRow(data, (value) =>
        formatTideHeightDisplay(value, unitsConfig),
      ),
    ],
    [data, unitsConfig],
  );

  const formatXTick = useMemo(
    () => createWeatherChartXTickFormatter(timeZone),
    [timeZone],
  );

  return (
    <TimeSeriesAreaChartRecharts
      data={data}
      domain={domain}
      timeZone={timeZone}
      colors={{
        line: WeatherTheme.colors.tides,
        fill: {
          top: WeatherTheme.colors.tidesfill,
          bottom: WeatherTheme.colors.tidesfill,
        },
        annotation: WeatherTheme.colors.tides,
        scrubberDot: WeatherTheme.colors.tides,
      }}
      annotations={annotations}
      plotHeight={WEATHER_CHART_PLOT_HEIGHT}
      xTickIntervalHours={3}
      hideMidnightTicks
      formatXTick={formatXTick}
      xTickColor={theme.colors.darkergrey}
      backgroundBands={backgroundBands}
      showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
      labelRows={labelRows}
      selectedTime={selectedTime}
      defaultSelectedTime={defaultSelectedTime}
      onSelectedTimeChange={onSelectedTimeChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
      plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
      chromeColors={WEATHER_CHART_CHROME_COLORS}
    />
  );
};

export default TideChart;
